import React from 'react';
import { Platform } from 'react-native';
import Badge from '~/components/Badge';
import { BadgeColorTheme } from '~/components/Badge/types';
import Button from '~/components/Button';
import InputField from '~/components/InputField';
import PDFViewer from '~/components/PDFViewer';
import PhotoSizeCell from '~/components/PhotoSizeCells';
import { Spacer } from '~/components/common/style.web';
import { PageFormat, PageType } from '~/data/types/graphql';
import { ModalScreenLayout } from '~/screens/style';
import { getFileSize } from '~/utils/helpers';
import { t } from '~/utils/i18n';
import { FileDetails } from '~/utils/image-picker';
import { PHOTO_FORMAT_CELL_WIDTH } from '../../ProfilePageSetup/layout/style';
import {
  ScrollView,
  Section,
  ImageContent,
  ImageBanner,
  DeleteIcon,
  DeleteIconContainer,
  ImageContainer,
  FormatSection,
  PhotoFormatContainer,
  PhotoFormatDescription,
  ButtonCreate,
  ImageDescriptionContainer,
  ImageDescription,
  NoImageUploaded,
  Row,
} from './style';

type CreateCustomPageLayoutProps = {
  onBack: () => void;
  onPickDocument: () => void;
  onPickImage: () => void;
  imgUri?: FileDetails;
  selectedPhotoFormat: PageFormat;
  setSelectedPhotoFormat: React.Dispatch<React.SetStateAction<PageFormat>>;
  customPageTitle: string;
  setCustomPageTitle: React.Dispatch<React.SetStateAction<string>>;
  onCreateCustomPage: () => void;
  onRemoveImage: () => void;
  createLoading?: boolean;
};

const photoFormats: PageFormat[] = [
  PageFormat.PORTRAIT,
  PageFormat.LANDSCAPE,
  PageFormat.CUSTOM,
];

export const ImageQualityBadgeColor: { [key: string]: BadgeColorTheme } = {
  GOOD: 'green',
  CRITICAL: 'tertiary',
  POOR: 'red',
};

export default function CreateCustomPage({
  onBack,
  onPickDocument,
  onPickImage,
  imgUri,
  selectedPhotoFormat,
  setSelectedPhotoFormat,
  customPageTitle,
  setCustomPageTitle,
  onCreateCustomPage,
  onRemoveImage,
  createLoading,
}: CreateCustomPageLayoutProps): JSX.Element {
  const isImgUriPdf = imgUri?.type === PageType.PDF;

  return (
    <ModalScreenLayout
      backButtonTestID={'buttonBack'}
      title={t('screens.createCustomPage')}
      onBackPress={onBack}
    >
      <ScrollView showsVerticalScrollIndicator={false}>
        <Section title={t('createCustomPage.titleSection')} />
        <InputField
          testID={'CustomPageTitle'}
          value={customPageTitle}
          onChangeText={(query) => setCustomPageTitle(query)}
          label={t('createCustomPage.titleInput')}
        />

        <ImageContainer>
          <ImageContent>
            {imgUri?.uri ? (
              isImgUriPdf ? (
                <PDFViewer
                  key={imgUri.uri}
                  uri={imgUri.uri}
                  hidePreview={true}
                  height={170}
                  width={121}
                  name={imgUri.name}
                />
              ) : (
                <ImageBanner
                  key={imgUri?.uri}
                  testID={`PagePhotoCategoryCardImage`}
                  source={{ uri: imgUri?.uri }}
                />
              )
            ) : (
              <NoImageUploaded />
            )}
          </ImageContent>
          {imgUri?.uri && (
            <DeleteIconContainer onPress={onRemoveImage}>
              <DeleteIcon />
            </DeleteIconContainer>
          )}
          <ImageDescriptionContainer>
            {imgUri?.uri && (
              <ImageDescription>
                {`${t('createCustomPage.imageType')} ${
                  imgUri?.type ? t(`createCustomPage.${imgUri.type}`) : ''
                }`}
              </ImageDescription>
            )}

            {imgUri?.uri && (
              <>
                <ImageDescription>{`${t('createCustomPage.imageSize')} ${
                  imgUri?.size ? getFileSize(imgUri?.size) : ''
                }`}</ImageDescription>
                <ImageDescription>
                  {imgUri?.quality && (
                    <Badge
                      colorTheme={
                        ImageQualityBadgeColor[imgUri?.quality as string]
                      }
                      text={imgUri?.quality as string}
                    />
                  )}
                </ImageDescription>
              </>
            )}
          </ImageDescriptionContainer>
        </ImageContainer>
        <Row>
          <Button
            testID="buttonUploadPdf"
            text={
              Platform.OS !== 'android'
                ? t('createCustomPage.uploadPdf')
                : t('createCustomPage.uploadPdfOrImage')
            }
            onPress={onPickDocument}
            type="secondary-base"
            size="sm"
          />
          {Platform.OS === 'ios' ? (
            <Button
              testID="buttonUploadImage"
              text={t('createCustomPage.uploadImage')}
              onPress={onPickImage}
              type="secondary-base"
              size="sm"
            />
          ) : null}
        </Row>
        <FormatSection title={t('createCustomPage.formatSection')} />
        <PhotoFormatContainer>
          {photoFormats.map((photoFormat) => (
            <PhotoSizeCell
              key={photoFormat}
              testID={`buttonFormat${photoFormat}`}
              format={photoFormat}
              selected={photoFormat === selectedPhotoFormat}
              width={PHOTO_FORMAT_CELL_WIDTH}
              onPress={() => setSelectedPhotoFormat(photoFormat)}
            />
          ))}
        </PhotoFormatContainer>
        <PhotoFormatDescription>
          {t('createCustomPage.formatDescription')}
        </PhotoFormatDescription>
        <Spacer v={183} />
        <ButtonCreate
          testID={'buttonCreate'}
          text={t('createCustomPage.createBtn')}
          onPress={onCreateCustomPage}
          size="lg"
          flex
          minWidth={360}
          loading={createLoading}
        />
        <Spacer v={20} />
      </ScrollView>
    </ModalScreenLayout>
  );
}
